// frontend/src/pages/NotFoundPage.tsx
import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  useTheme,
} from '@mui/material';
import {
  Home,
  ArrowBack,
  LocalHospital,
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import PageSEO from '../components/SEO/PageSEO';

import { useLanguage } from '../hooks/useLanguage';

const MotionBox = motion(Box);

const NotFoundPage: React.FC = () => {
  const theme = useTheme();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
  };

  return (
    <>
      <PageSEO
        title={t('notFound.title')}
        description={t('notFound.description')}
        path="/404"
      />

      <Container maxWidth="sm">
        <MotionBox
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          sx={{
            minHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            py: 8,
          }}
        >
          {/* 404 Number */}
          <MotionBox variants={itemVariants} sx={{ mb: 4 }}>
            <Typography
              sx={{
                fontSize: { xs: '8rem', md: '12rem' },
                fontWeight: 900,
                lineHeight: 0.8,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 4px 20px rgba(0,0,0,0.1)',
              }}
            >
              404
            </Typography>
          </MotionBox>

          {/* Medical Icon */}
          <MotionBox
            variants={itemVariants}
            sx={{ mb: 4 }}
            animate={{
              rotate: [0, 5, -5, 0],
              transition: {
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
              },
            }}
          >
            <Box
              sx={{
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${theme.palette.primary.main}20, ${theme.palette.secondary.main}20)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: `3px solid ${theme.palette.primary.main}30`,
              }}
            >
              <LocalHospital sx={{ fontSize: 50, color: 'primary.main' }} />
            </Box>
          </MotionBox>

          {/* Error Message */}
          <MotionBox variants={itemVariants} sx={{ mb: 2 }}>
            <Typography
              variant="h3"
              component="h1"
              sx={{ mb: 2, fontWeight: 700 }}
            >
              {t('notFound.title')}
            </Typography>
            
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ mb: 4, maxWidth: 400, mx: 'auto', lineHeight: 1.6 }}
            >
              {t('notFound.description')}
            </Typography>
          </MotionBox>

          {/* Action Buttons */}
          <MotionBox
            variants={itemVariants}
            sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}
          >
            <Button
              variant="contained"
              size="large"
              startIcon={<Home />}
              component={Link}
              to="/"
              sx={{
                borderRadius: 3,
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                boxShadow: 3,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 6,
                },
                transition: 'all 0.3s ease',
              }}
            >
              {t('notFound.goHome')}
            </Button>
            
            <Button
              variant="outlined"
              size="large"
              startIcon={<ArrowBack />}
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 3,
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2,
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              {t('common.back')}
            </Button>
          </MotionBox>

          {/* Helpful Links */}
          <MotionBox variants={itemVariants} sx={{ mt: 6 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Возможно, вас заинтересует:
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
              <Button
                component={Link}
                to="/departments"
                variant="text"
                sx={{ textDecoration: 'underline' }}
              >
                Наши отделения
              </Button>
              
              <Button
                component={Link}
                to="/doctors"
                variant="text"
                sx={{ textDecoration: 'underline' }}
              >
                Врачи
              </Button>
              
              <Button
                component={Link}
                to="/services"
                variant="text"
                sx={{ textDecoration: 'underline' }}
              >
                Услуги
              </Button>
              
              <Button
                component={Link}
                to="/contact"
                variant="text"
                sx={{ textDecoration: 'underline' }}
              >
                Контакты
              </Button>
            </Box>
          </MotionBox>
        </MotionBox>
      </Container>
    </>
  );
};

export default NotFoundPage;